import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";

import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import {
  ActiveInactiveDestination,
  deleteDestination,
  getAllDestination,
} from "./services/destination.services";
import { clearDestinationID, setDestinationID } from "./destinationSlice";
import { Tooltip } from "@mui/material";
import { FaGraduationCap } from "react-icons/fa6";

const Destination: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllDestination());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const handleCreate = () => {
    navigate("/destination/create");
  };

  return (
    <>
      <DeleteDestinationDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Destination List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              {"Create Destination"}
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <DestinationGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const DestinationGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const DestinationList = useAppSelector(
    (state) => state.destination.DestinationList
  );
  const loading = useAppSelector((state) => state.destination.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const [expandedRow, setExpandedRow] = useState(null);
  const truncateDescription = (Description: string) => {
    if (!Description) return "";
    const maxLength = 100;
    if (Description.length <= maxLength) return Description;
    return Description.substring(0, maxLength) + "...";
  };

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? DestinationList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (ID: number) => {
      navigate("/destination/edit", { state: { DestinationID: ID } });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteDestination"));
      dispatch(setDestinationID(ID));
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            themeColor="success"
            title="Edit"
            onClick={() => handleEdit(props.dataItem.ID)}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 5 || props.index === 6 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(
      props.dataItem.IsActive ?? false
    );

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(ActiveInactiveDestination(data)).then(() => {
        dispatch(getAllDestination());
        // if (response.payload?.data?.Status === 200) {
        //   dispatch(getAllBankes());
        // } else {
        //   dispatch(getAllBankes());
        // }
      });
    };

    return (
      <td
        style={{ textAlign: "center", ...props.style }}
        className={props.className}
      >
        <Switch
          disabled={isEditAccess ? false : true}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {DestinationList && DestinationList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          className="responsive-table"
          filter={filter}
          data={
            filter
              ? filterBy(DestinationList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : DestinationList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={DestinationList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="Title"
            title="Title"
            width={"300px"}
            cell={(props: any) => (
              <td>
                {isEditAccess ? (
                  <div
                    className="hoverableName"
                    onClick={() =>
                      navigate("/destination/edit", {
                        state: { DestinationID: props.dataItem?.ID },
                      })
                    }
                  >
                    {props.dataItem?.Title}
                  </div>
                ) : (
                  <div className="normalName">{props.dataItem?.Title}</div>
                )}
              </td>
            )}
          />
          <Column
            field="Description"
            title="Description"
            width={"300px"}
            cell={(props: any) => (
              <td>
                <div
                  className="remarks-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      expandedRow === props.dataItem?.ID
                        ? props.dataItem?.Description
                        : truncateDescription(props.dataItem?.Description),
                  }}
                />
                {props.dataItem?.Description?.length > 100 && (
                  <span
                    className="readmore-btn"
                    style={{
                      textAlign: "right",
                    }}
                    onClick={() =>
                      setExpandedRow(
                        expandedRow === props.dataItem?.ID
                          ? null
                          : props.dataItem?.ID
                      )
                    }
                  >
                    <u>
                      {expandedRow === props.dataItem?.ID
                        ? "Read Less"
                        : "Read More"}
                    </u>
                  </span>
                )}
              </td>
            )}
          />

          <Column field="DestinationURL" title="Destination URL" width={200} />
          <Column
            filterable={false}
            field="DestinationLogo"
            title="Logo"
            width={110}
            cell={(props: any) => (
              <td style={{ textAlign: "center" }}>
                <Tooltip
                  title={
                    <img
                      src={props.dataItem?.DestinationLogo}
                      alt="destination logo"
                      width={250}
                      onClick={() =>
                        window.open(props.dataItem?.DestinationLogo)
                      }
                      height={250}
                    />
                  }
                >
                  {props.dataItem?.DestinationLogo ? (
                    <img
                      style={{ border: "1px solid grey", padding: "1px" }}
                      src={props.dataItem?.DestinationLogo}
                      alt="destination logo"
                      width={50}
                      onClick={() =>
                        window.open(props.dataItem?.DestinationLogo)
                      }
                      height={50}
                    />
                  ) : (
                    <>
                      <FaGraduationCap
                        style={{
                          fontSize: "50px",
                          background: "lightgray",
                          padding: "5px",
                          borderRadius: "4px",
                          color: "#163064",
                        }}
                      />
                    </>
                  )}
                </Tooltip>
              </td>
            )}
          />
          <Column
            filterable={false}
            field="DestinationImg"
            title="Image"
            width={110}
            cell={(props: any) => (
              <td style={{ textAlign: "center" }}>
                <Tooltip
                  title={
                    <img
                      src={props.dataItem?.DestinationImg}
                      alt="destination image"
                      width={250}
                      onClick={() =>
                        window.open(props.dataItem?.DestinationImg)
                      }
                      height={250}
                    />
                  }
                >
                  {props.dataItem?.DestinationImg ? (
                    <img
                      style={{ border: "1px solid grey", padding: "1px" }}
                      src={props.dataItem?.DestinationImg}
                      alt="destination image"
                      width={50}
                      onClick={() =>
                        window.open(props.dataItem?.DestinationImg)
                      }
                      height={50}
                    />
                  ) : (
                    <>
                      <FaGraduationCap
                        style={{
                          fontSize: "50px",
                          background: "lightgray",
                          padding: "5px",
                          borderRadius: "4px",
                          color: "#163064",
                        }}
                      />
                    </>
                  )}
                </Tooltip>
              </td>
            )}
          />

          <Column
            field="ID"
            title="Status"
            cell={MyActiveInActtiveCommandCell}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              filterable={false}
              locked={true}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteDestinationDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const DestinationID = useAppSelector(
    (state) => state.destination.DestinationID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteDestination(ID));
      if (response.payload.Data.Status === 200) {
        dispatch(getAllDestination());
        dispatch(closeDialog());
        dispatch(clearDestinationID());
      } else {
        dispatch(getAllDestination());
        dispatch(closeDialog());
        dispatch(clearDestinationID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteDestination" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Destination"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Destination ?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(DestinationID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Destination;
